import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { DetailsCard, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import {
  CheckInCustomerCorrectInfo,
  CheckInOptionalService,
  CommunicationRemarks,
  ServiceTrackerRating,
  getEmail,
  getKeylockerPhoneNumber,
  getKioskPhoneNumber,
  getPhone,
  getPreferredChannel
} from "modules/AppointmentDetails/components/CustomerActions/components";
import { useCheckInResultMutation } from "modules/AppointmentDetails/components/CustomerActions/components/CheckInOptionalService/useCheckInResultMutation";
import {
  useCheckInRemarkMutation,
  useDiagnoseOverviewRemarkMutation,
  useKeylockerRemarkMutation,
  useKioskRemarkMutation
} from "modules/AppointmentDetails/components/CustomerActions/components/CommunicationRemarks/hooks";
import "modules/AppointmentDetails/components/CustomerActions/CustomerActions.scss";

import type { SectionMenu } from "components";
import type { Appointment, CheckInRemark, CheckInResult, DiagnoseOverviewRemark, KeyLockerRemark, KioskRemark } from "models";

type CustomerActionsProps = {
  isExpanded: boolean;
  appointment: Appointment;
  sectionMenu: SectionMenu;
};

const getCustomerActions = (appointment: Appointment) => ({
  correctInfo: [getEmail(appointment), getPhone(appointment), getKeylockerPhoneNumber(appointment), getKioskPhoneNumber(appointment)].filter((ci): ci is string => !!ci),
  preferredChannel: getPreferredChannel(appointment),
  checkInResults: appointment.customer_communication?.check_in_results?.filter(r => r.accepted) ?? [],
  checkInRemarks: appointment.customer_communication?.check_in_remarks ?? [],
  diagnoseOverviewRemarks: appointment.customer_communication?.diagnose_overview_remarks ?? [],
  keylockerRemarks:
    appointment.keylocker_communications?.reduce<KeyLockerRemark[]>((acc, kc) => {
      if (kc.remark) acc.push(kc.remark);
      return acc;
    }, []) ?? [],
  kioskRemarks:
    appointment.kiosk_communications?.reduce<KioskRemark[]>((acc, kc) => {
      if (kc.remark) acc.push(kc.remark);
      return acc;
    }, []) ?? [],
  serviceTrackerRating: {
    star_rating_experience: appointment.customer_communication?.star_rating_experience,
    star_rating_service: appointment.customer_communication?.star_rating_service,
    star_rating_speed: appointment.customer_communication?.star_rating_speed,
    star_rating_feedback: appointment.customer_communication?.star_rating_feedback
  }
});

export const getCustomerActionsCount = (appointment?: Appointment) => {
  if (!appointment) return 0;

  const { serviceTrackerRating, preferredChannel, ...customerActions } = getCustomerActions(appointment);
  const hasRating = serviceTrackerRating.star_rating_experience || serviceTrackerRating.star_rating_service || serviceTrackerRating.star_rating_speed;

  return Object.values(customerActions).reduce((count, action) => count + action.length, 0) + (hasRating ? 1 : 0) + (preferredChannel ? 1 : 0);
};

export const CustomerActions = ({ isExpanded, appointment, sectionMenu }: CustomerActionsProps) => {
  const { t } = useTranslation();

  const [isSectionExpanded, setIsSectionExpanded] = useState(isExpanded);

  const { mutate: handleCheckInResult } = useCheckInResultMutation();
  const canCreateIntervention = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Interventions);
  const canHandleCheckInResult = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.CheckInServiceAccepted);
  const canHandleCheckInRemark = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.CheckInServiceRemarkHandled);
  const canHandleDiagnoseOverviewRemark = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.DiagnoseOverviewRemarks);
  const kioskRemarkMutation = useKioskRemarkMutation();
  const checkInRemarkMutation = useCheckInRemarkMutation();
  const keylockerRemarkMutation = useKeylockerRemarkMutation();
  const diagnoseOverviewRemarkMutation = useDiagnoseOverviewRemarkMutation();

  const { correctInfo, preferredChannel, checkInResults, checkInRemarks, diagnoseOverviewRemarks, keylockerRemarks, kioskRemarks } = getCustomerActions(appointment);

  const hasActionsToShow =
    !!correctInfo.length ||
    !!preferredChannel ||
    checkInResults.some(r => !r.receptionist_handled) ||
    checkInRemarks.some(r => !r.receptionist_handled) ||
    diagnoseOverviewRemarks.some(r => !r.receptionist_handled) ||
    keylockerRemarks.some(r => !r.receptionist_handled) ||
    kioskRemarks.some(r => !r.receptionist_handled);

  useEffect(() => {
    if (!appointment.customer_communication) return;
    if (isExpanded && !hasActionsToShow) setIsSectionExpanded(false);
  }, []);

  useEffect(() => {
    setIsSectionExpanded(isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    if (hasActionsToShow) setIsSectionExpanded(true);
  }, [hasActionsToShow]);

  const numberOfActions = sectionMenu.count ?? 0;
  if (!numberOfActions) return;

  const onConvertedCheckInRemarkToInvervention = (remark: CheckInRemark) => checkInRemarkMutation.mutate({ checkin_remark_id: remark.id, handled: true });
  const onConvertedCheckInResultToInvervention = (result: CheckInResult) => handleCheckInResult({ checkin_result_id: result.id, handled: true });
  const onConvertedDiagnoseOverviewRemarkToInvervention = (remark: DiagnoseOverviewRemark) =>
    canHandleDiagnoseOverviewRemark && diagnoseOverviewRemarkMutation.mutate({ diagnose_overview_remark_id: remark.id, handled_remark: true });
  const onConvertedKeylockerRemarkToInvervention = (remark: KeyLockerRemark) =>
    keylockerRemarkMutation.mutate({
      keylocker_communication_id: remark.keylocker_communication_id,
      keylocker_remark_id: remark.id,
      handled_remark: true
    });
  const onConvertedKioskRemarkToInvervention = (remark: KioskRemark) =>
    kioskRemarkMutation.mutate({
      kiosk_communication_id: remark.kiosk_communication_id,
      remark_id: remark.id,
      handled_remark: true
    });

  const handleToggleCheckinRemark = (remark: CheckInRemark) => checkInRemarkMutation.mutate({ checkin_remark_id: remark.id, handled: !remark.receptionist_handled });
  const handleToggleDiagnoseRemark = (remark: DiagnoseOverviewRemark) =>
    diagnoseOverviewRemarkMutation.mutate({ diagnose_overview_remark_id: remark.id, handled_remark: !remark.receptionist_handled });
  const handleToggleKeylockerRemark = (remark: KeyLockerRemark) =>
    keylockerRemarkMutation.mutate({
      keylocker_communication_id: remark.keylocker_communication_id,
      keylocker_remark_id: remark.id,
      handled_remark: !remark.receptionist_handled
    });
  const handleToggleKioskRemark = (remark: KioskRemark) =>
    kioskRemarkMutation.mutate({ kiosk_communication_id: remark.kiosk_communication_id, remark_id: remark.id, handled_remark: !remark.receptionist_handled });

  const handleToggleCheckinResult = (result: CheckInResult) => handleCheckInResult({ checkin_result_id: result.id, handled: !result.receptionist_handled });

  return (
    <>
      <DetailsCard sectionMenuId={sectionMenu.id} title={`${sectionMenu.title} (${numberOfActions})`} icon="calendar check regular" isExpanded={isSectionExpanded}>
        <CheckInCustomerCorrectInfo correctInfo={correctInfo} preferredChannel={preferredChannel} />
        <ServiceTrackerRating customerCommunication={appointment.customer_communication} />

        <div className="CustomerActions">
          <CheckInOptionalService
            appointment={appointment}
            title={t("v8_online_check_in_service", "Online Check-in service")}
            results={checkInResults}
            canCreateIntervention={canCreateIntervention}
            canToggleHandled={canHandleCheckInResult}
            onToggleHandled={handleToggleCheckinResult}
            onInterventionConverted={onConvertedCheckInResultToInvervention}
          />
          <div className="CCRemarks">
            <CommunicationRemarks
              appointment={appointment}
              title={t("v8_online_check_in_remarks", "Online Check-in remarks")}
              remarks={checkInRemarks}
              remarkIdProperty="check_in_remark_id"
              canCreateIntervention={canCreateIntervention}
              canToggleHandled={canHandleCheckInRemark}
              onToggleHandled={handleToggleCheckinRemark}
              onInterventionConverted={onConvertedCheckInRemarkToInvervention}
            />
            <CommunicationRemarks
              appointment={appointment}
              title={t("v8_diagnose_overview_remarks", "Diagnose overview remarks")}
              remarks={diagnoseOverviewRemarks}
              remarkIdProperty="diagnose_overview_remark_id"
              canCreateIntervention={canCreateIntervention}
              canToggleHandled={canHandleDiagnoseOverviewRemark}
              onToggleHandled={handleToggleDiagnoseRemark}
              onInterventionConverted={onConvertedDiagnoseOverviewRemarkToInvervention}
            />
            <CommunicationRemarks
              appointment={appointment}
              title={t("v8_keylocker_remarks", "Keylocker remarks")}
              remarks={keylockerRemarks}
              remarkIdProperty="keylocker_remark_id"
              color="purple"
              icon="key"
              canCreateIntervention
              canToggleHandled
              onToggleHandled={handleToggleKeylockerRemark}
              onInterventionConverted={onConvertedKeylockerRemarkToInvervention}
            />
            <CommunicationRemarks
              appointment={appointment}
              title={t("v8_kiosk_remarks", "Kiosk remarks")}
              remarks={kioskRemarks}
              remarkIdProperty="kiosk_remark_id"
              color="green"
              canCreateIntervention
              canToggleHandled
              onToggleHandled={handleToggleKioskRemark}
              onInterventionConverted={onConvertedKioskRemarkToInvervention}
            />
          </div>
        </div>
      </DetailsCard>
    </>
  );
};
